import { useCallback, useId, useMemo, useState } from 'react'

import { ResponsiveBar } from '@nivo/bar'
import { ResponsiveFunnel } from '@nivo/funnel'
import { ResponsiveLine } from '@nivo/line'
import { arc as d3Arc, pie as d3Pie } from 'd3-shape'

import { MARGIN_LEFT } from '@/modules/report/views/bucket/components/LineChart'
import { useChartTheme } from '@/modules/report/views/bucket/components/LineChart/useChartTheme'

import { getChartAxisFormatter, getChartLegendLabel, getChartValueFormatter } from '../utils/chartFormatters'

const MARGIN_RIGHT = 24

const CHART_COLORS = ['#3b82f6', '#22c55e', '#ef4444', '#a855f7', '#f59e0b', '#06b6d4']

const AXIS_LABELS = {
  money: 'Value',
  percentage: '%',
  count: 'Count',
}

const CHART_WRAPPER_CLASS = 'relative flex w-full flex-col aspect-[3/2] min-h-90 max-h-[480px] overflow-hidden rounded border twy-border-base twy-bg-base p-1'

const CHART_AREA_CLASS = 'relative min-h-0 flex-1'

const CHART_SCROLL_CLASS = 'relative min-h-0 flex-1 overflow-x-auto overflow-y-hidden'

const PX_PER_BUCKET = 40

const TICK_LABEL_MAX_LENGTH = 14

const AXIS_LABEL_CLASS = 'shrink-0 text-[11px] twy-text-base-muted'

const PLOT_LEFT_PADDING = 8

function truncateTickLabel(value) {
  const label = String(value ?? '')
  if (label.length <= TICK_LABEL_MAX_LENGTH) return label

  return `${label.slice(0, TICK_LABEL_MAX_LENGTH - 1)}…`
}

function ChartScrollArea({ minWidth, children }) {
  return (
    <div className={CHART_SCROLL_CLASS}>
      <div className="h-full w-full" style={{ minWidth }}>
        {children}
      </div>
    </div>
  )
}

const scrollbarHeight = { value: null }

function getScrollbarHeight() {
  if (scrollbarHeight.value !== null) return scrollbarHeight.value

  const probe = document.createElement('div')
  probe.style.position = 'absolute'
  probe.style.visibility = 'hidden'
  probe.style.overflowX = 'scroll'
  probe.style.width = '100px'
  probe.style.height = '100px'

  document.body.appendChild(probe)
  scrollbarHeight.value = probe.offsetHeight - probe.clientHeight
  probe.remove()

  return scrollbarHeight.value
}

const HIDDEN_TOOLTIP = { display: 'none' }

function HoverTooltip({ hovered, format }) {
  if (!hovered) return null

  return (
    <div
      className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-[calc(100%+14px)]"
      style={{ left: hovered.x, top: hovered.y }}
    >
      <ChartTooltip
        title={hovered.title}
        color={hovered.color}
        label={hovered.label}
        value={hovered.value}
        format={format}
      />
    </div>
  )
}

function AxisFrame({ valueLabel, indexLabel, axisWidth, axis, children }) {
  return (
    <div className="flex min-h-0 flex-1">
      <span className={`${AXIS_LABEL_CLASS} self-center rotate-180 px-1 [writing-mode:vertical-rl]`}>
        {valueLabel}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1">
          <div
            className="pointer-events-none shrink-0"
            style={{ width: axisWidth, paddingBottom: getScrollbarHeight() }}
          >
            {axis}
          </div>
          {children}
        </div>
        <span className={`${AXIS_LABEL_CLASS} pt-1 text-center`}>
          {indexLabel}
        </span>
      </div>
    </div>
  )
}

const FUNNEL_MARGIN = { top: 16, right: 24, bottom: 16, left: 24 }

const CHART_MARGIN = {
  top: 16,
  right: MARGIN_RIGHT,
  bottom: 100,
  left: MARGIN_LEFT.count,
}

function toLineSeries(data = [], keys = []) {
  return keys.map(key => ({
    id: key,
    data: data.map(row => ({
      x: row.date,
      y: Number(row[key]) || 0,
    })),
  }))
}

function buildLineGradients(keys = [], instanceId) {
  const gradientId = index => `chart-${instanceId}-gradient-${index}`

  const defs = keys.map((key, index) => ({
    id: gradientId(index),
    type: 'linearGradient',
    colors: [
      { offset: 0, color: CHART_COLORS[index % CHART_COLORS.length], opacity: 0.35 },
      { offset: 100, color: CHART_COLORS[index % CHART_COLORS.length], opacity: 0 },
    ],
  }))

  const fill = keys.map((key, index) => ({
    match: { id: key },
    id: gradientId(index),
  }))

  return { defs, fill }
}

function ChartLegend({ keys, legendLabel, colorFor }) {
  return (
    <div className="shrink-0 flex gap-4 overflow-x-auto px-2 py-1.5">
      {keys.map(key => <div
        key={key}
        className="flex shrink-0 items-center gap-1.5 text-sm twy-text-base-muted"
      >
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: colorFor(key) }}
        />
        <span className="whitespace-nowrap">{legendLabel(key)}</span>
      </div>)}
    </div>
  )
}

function ChartTooltip({ title, color, label, value, format }) {
  const formattedValue = format ? format(value) : value

  return (
    <div className="rounded-xs twy-bg-base-accent twy-shadow-base twy-text-base-accent px-2.5 py-1.5 text-sm">
      <div className="twy-text-base mb-1">{title}</div>
      <div className="flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />
        <span>{label}: {formattedValue}</span>
      </div>
    </div>
  )
}

const RADIUS = 100
const PADDING = 4
const VIEW_SIZE = (RADIUS + PADDING) * 2
const LABEL_MIN_SHARE = 0.06

function formatShare(value, total) {
  if (!total) return '0%'
  return `${((value / total) * 100).toFixed(1).replace('.0', '')}%`
}

function PieChart({ slices = [], colorFor, legendLabel, format }) {
  const { isDark, textColor } = useChartTheme()
  const [hovered, setHovered] = useState(null)

  const total = useMemo(
    () => slices.reduce((sum, slice) => sum + (Number(slice.value) || 0), 0),
    [slices],
  )

  const arcs = useMemo(() => {
    const layout = d3Pie().sort(null).sortValues(null).value(slice => Number(slice.value) || 0)
    const buildArc = d3Arc().innerRadius(0).outerRadius(RADIUS).padAngle(0.004)
    const buildLabelArc = d3Arc().innerRadius(RADIUS * 0.62).outerRadius(RADIUS * 0.62)

    return layout(slices).map(datum => ({
      id: datum.data.id,
      value: Number(datum.data.value) || 0,
      path: buildArc(datum),
      centroid: buildLabelArc.centroid(datum),
      share: (datum.endAngle - datum.startAngle) / (Math.PI * 2),
    }))
  }, [slices])

  const handleMove = useCallback((event, slice) => {
    const bounds = event.currentTarget.closest('[data-pie-chart]').getBoundingClientRect()

    setHovered({
      ...slice,
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    })
  }, [])

  if (total <= 0) {
    return <div className="font-semibold py-8 text-center">No results to show.</div>
  }

  return (
    <div
      data-pie-chart
      className="relative h-full w-full"
      onMouseLeave={() => setHovered(null)}
    >
      <svg
        viewBox={`${-RADIUS - PADDING} ${-RADIUS - PADDING} ${VIEW_SIZE} ${VIEW_SIZE}`}
        className="h-full w-full"
      >
        {arcs.map(slice => <path
          key={slice.id}
          d={slice.path}
          fill={colorFor(slice.id)}
          stroke={isDark ? '#1d2830' : '#ffffff'}
          strokeWidth={1}
          opacity={hovered && hovered.id !== slice.id ? 0.45 : 1}
          onMouseMove={event => handleMove(event, slice)}
        />)}
        {arcs
          .filter(slice => slice.share >= LABEL_MIN_SHARE)
          .map(slice => <text
            key={`label-${slice.id}`}
            x={slice.centroid[0]}
            y={slice.centroid[1]}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={10}
            fill={textColor}
            className="pointer-events-none select-none"
          >
            {formatShare(slice.value, total)}
          </text>)}
      </svg>
      {hovered && <div
        className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[calc(100%+8px)]"
        style={{ left: hovered.x, top: hovered.y }}
      >
        <ChartTooltip
          title={`${formatShare(hovered.value, total)} of total`}
          color={colorFor(hovered.id)}
          label={legendLabel(hovered.id)}
          value={hovered.value}
          format={format}
        />
      </div>}
    </div>
  )
}

export default function ChartResult({ dataType, result, collection }) {
  const [hovered, setHovered] = useState(null)
  const instanceId = useId().replace(/[^a-zA-Z0-9]/g, '')
  const { nivoTheme, gridStroke } = useChartTheme()
  const {
    data = [],
    slices = [],
    keys = [],
    type = 'count',
    format,
    keyType = 'metric',
    indexLabel = '',
  } = result || {}

  const valueFormat = format || getChartValueFormatter(type)
  const axisFormat = getChartAxisFormatter(type)
  const legendLabel = key => getChartLegendLabel(collection, key, keyType)

  const colorFor = key => {
    const index = keys.indexOf(key)
    return CHART_COLORS[(index < 0 ? 0 : index) % CHART_COLORS.length]
  }

  const funnelData = useMemo(
    () => slices.map(slice => ({
      id: slice.id,
      value: slice.value,
      label: getChartLegendLabel(collection, slice.id, keyType),
    })),
    [slices, collection, keyType],
  )

  const funnelShareLabel = stage => {
    const [first] = funnelData
    if (!first) return ''
    if (first.id === stage.id) return 'First step'
    if (!first.value) return ''

    const share = (stage.value / first.value) * 100
    return `${share.toFixed(1).replace('.0', '')}% of ${first.label}`
  }

  const lineData = useMemo(() => toLineSeries(data, keys), [data, keys])

  const scrollWidth = data.length * PX_PER_BUCKET
  const barScrollWidth = data.length * Math.max(PX_PER_BUCKET, keys.length * 14)

  const lineYMin = useMemo(
    () => Math.min(0, ...lineData.flatMap(serie => serie.data.map(point => point.y))),
    [lineData],
  )
  const lineGradients = useMemo(() => buildLineGradients(keys, instanceId), [keys, instanceId])
  const leftMargin = MARGIN_LEFT[type] || MARGIN_LEFT.count
  const axisLabel = AXIS_LABELS[type] || type

  const chartTheme = useMemo(() => ({
    ...nivoTheme,
    grid: {
      line: {
        stroke: gridStroke,
        strokeWidth: 1,
      },
    },
  }), [nivoTheme, gridStroke])

  const scrollChartTheme = useMemo(
    () => ({ ...chartTheme, tooltip: HIDDEN_TOOLTIP }),
    [chartTheme],
  )

  const handleHoverEnd = useCallback(() => setHovered(null), [])

  const handleLineHover = useCallback((point, event) => {
    setHovered({
      title: point.data.x,
      label: getChartLegendLabel(collection, point.serieId, keyType),
      value: point.data.y,
      color: point.serieColor,
      x: event.clientX,
      y: event.clientY,
    })
  }, [collection, keyType])

  const handleBarHover = useCallback((datum, event) => {
    setHovered({
      title: datum.indexValue,
      label: getChartLegendLabel(collection, datum.id, keyType),
      value: datum.value,
      color: datum.color,
      x: event.clientX,
      y: event.clientY,
    })
  }, [collection, keyType])

  const axisBottom = {
    tickSize: 0,
    tickPadding: 10,
    tickRotation: -90,
    format: truncateTickLabel,
  }

  const axisLeft = {
    tickSize: 5,
    tickPadding: 10,
    tickRotation: 0,
    format: value => axisFormat(value),
  }

  const lineMargin = {
    ...CHART_MARGIN,
    left: PLOT_LEFT_PADDING,
  }

  const axisOverlayMargin = {
    ...CHART_MARGIN,
    left: leftMargin - 1,
    right: 0,
  }

  if (!data.length || !keys.length) {
    return <div className="font-semibold py-8 text-center">No results to show.</div>
  }

  if (dataType === 'funnel-chart') {
    return (
      <div className={CHART_WRAPPER_CLASS}>
        <ChartLegend keys={keys} legendLabel={legendLabel} colorFor={colorFor} />
        <div className={CHART_AREA_CLASS}>
          <ResponsiveFunnel
            data={funnelData}
            theme={chartTheme}
            margin={FUNNEL_MARGIN}
            colors={part => colorFor(part.id)}
            valueFormat={value => valueFormat(value)}
            interpolation="smooth"
            shapeBlending={0.8}
            borderWidth={0}
            labelColor="#ffffff"
            beforeSeparatorLength={10}
            afterSeparatorLength={10}
            currentBorderWidth={4}
            tooltip={({ part }) => <ChartTooltip
              title={funnelShareLabel(part.data)}
              color={part.color}
              label={legendLabel(part.data.id)}
              value={part.data.value}
              format={valueFormat}
            />
            }
          />
        </div>
      </div>
    )
  }

  if (dataType === 'pie-chart') {
    return (
      <div className={CHART_WRAPPER_CLASS}>
        <ChartLegend keys={keys} legendLabel={legendLabel} colorFor={colorFor} />
        <div className={CHART_AREA_CLASS}>
          <PieChart
            slices={slices}
            colorFor={colorFor}
            legendLabel={legendLabel}
            format={valueFormat}
          />
        </div>
      </div>
    )
  }

  if (dataType === 'line-chart') {
    return (
      <div className={CHART_WRAPPER_CLASS}>
        <ChartLegend keys={keys} legendLabel={legendLabel} colorFor={colorFor} />
        <AxisFrame
          valueLabel={axisLabel}
          indexLabel={indexLabel}
          axisWidth={leftMargin}
          axis={<ResponsiveLine
            data={lineData}
            theme={chartTheme}
            margin={axisOverlayMargin}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', stacked: false, min: lineYMin, max: 'auto' }}
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            axisLeft={axisLeft}
            enableGridX={false}
            enableGridY={false}
            layers={['axes']}
          />}
        >
          <ChartScrollArea minWidth={scrollWidth}>
            <ResponsiveLine
              data={lineData}
              colors={({ id }) => colorFor(id)}
              theme={scrollChartTheme}
              margin={lineMargin}
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear', stacked: false, min: lineYMin, max: 'auto' }}
              curve="monotoneX"
              axisTop={null}
              axisRight={null}
              axisBottom={axisBottom}
              axisLeft={null}
              enableGridY
              enableGridX={false}
              enablePoints
              pointSize={8}
              pointBorderWidth={0}
              lineWidth={2}
              enableArea
              areaOpacity={1}
              defs={lineGradients.defs}
              fill={lineGradients.fill}
              enableCrosshair
              useMesh
              crosshairType="x"
              onMouseMove={handleLineHover}
              onMouseLeave={handleHoverEnd}
            />
          </ChartScrollArea>
        </AxisFrame>
        <HoverTooltip hovered={hovered} format={valueFormat} />
      </div>
    )
  }

  return (
    <div className={CHART_WRAPPER_CLASS}>
      <ChartLegend keys={keys} legendLabel={legendLabel} colorFor={colorFor} />
      <AxisFrame
        valueLabel={axisLabel}
        indexLabel={indexLabel}
        axisWidth={leftMargin}
        axis={<ResponsiveBar
          data={data}
          keys={keys}
          indexBy="date"
          groupMode="grouped"
          theme={chartTheme}
          margin={axisOverlayMargin}
          axisTop={null}
          axisRight={null}
          axisBottom={null}
          axisLeft={axisLeft}
          enableGridY={false}
          enableLabel={false}
          layers={['axes']}
        />}
      >
        <ChartScrollArea minWidth={barScrollWidth}>
          <ResponsiveBar
            data={data}
            keys={keys}
            indexBy="date"
            groupMode="grouped"
            colors={({ id }) => colorFor(id)}
            onMouseEnter={handleBarHover}
            onMouseMove={handleBarHover}
            onMouseLeave={handleHoverEnd}
            margin={{
              ...CHART_MARGIN,
              left: 0,
            }}
            padding={0.3}
            innerPadding={2}
            borderRadius={2}
            theme={scrollChartTheme}
            axisBottom={axisBottom}
            axisLeft={null}
            enableGridY
            enableGridX={false}
            enableLabel={false}
          />
        </ChartScrollArea>
      </AxisFrame>
      <HoverTooltip hovered={hovered} format={valueFormat} />
    </div>
  )
}
